import { Box } from "@mui/material";
import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { apiGetRequest, apiPostRequest } from "../../Helpers/Requests";
// ⚠️ TEMP DEV-PHASE PREVIEW — remove with the KYC preview seed.
import { readKycPreviewFlag } from "../../Constants/kycPreview";

import endpoints from "../../Helpers/Endpoints";
import { useSnackbar } from "../../Components/SnackBar";
import LeaveCommunityModal from "./LeaveCommunityModal";
import PauseCommunityModal from "./PauseCommunityModal";
import SupplierJoinOffersModal from "../../Components/Modals/SupplierJoinOffersModal";
import ContentBody from "./components/ContentBody";
import { API_RECORDS_COUNT, SORT_ORDER } from "../../Constants/config";
import useInfiniteScroll from "../../hooks/useInfiniteScroll";
import { useTranslation } from "react-i18next";
import NeedPricelistModal from "../../Components/Modals/NeedPricelistModal";
import JoinRequestReceivedModal from "../../Components/Modals/JoinRequestReceivedModal";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { AppContext } from "../../Context/AppContext";
import {
  getGreetedJoinRequestKeys,
  getJoinRequestKey,
  markJoinRequestGreeted,
} from "../../Utils/joinRequestGreeting";

// ⚠️ TEMP DEV-PHASE PREVIEW — one invitation to look at while the KYC
// states are being reviewed without a backend.
const PREVIEW_JOIN_REQUESTS = [
  {
    id: "preview-1",
    community_id: "preview-1",
    community_name: "קהילה לדוגמה - אמהות הרצליניות",
    community_image: "",
    member_count: 1240,
    location: "ישראל, הרצליה",
    tags: ["אמהות", "משפחה", "שכונה"],
    status: "join_request",
    request_date: "01/09/2026",
    community_manager: {
      email: "manager@example.com",
      phone_prefix: "050",
      phone_number: "1234567",
    },
  },
];

const MyCommunitiesContainer = () => {
  const showSnackbar = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { profileData } = useContext(AppContext);
  const inviteCommunityId = searchParams.get("invite_community_id");
  // The greeting can only run once the stats have loaded, and that regularly
  // lands AFTER the cleanup effect below has stripped the param from the URL —
  // at which point `inviteCommunityId` is already null and the greeting falls
  // back to the once-per-request path, silently. Capture the arriving value at
  // mount so "came here to answer THIS invitation" survives the cleanup.
  const arrivedForCommunityRef = useRef(searchParams.get("invite_community_id"));
  const { t } = useTranslation("my-communities");
  const [activeTab, setActiveTab] = useState(
    inviteCommunityId ? 2 : (location.state?.activeTab ?? 1)
  );
  const inviteHandledRef = useRef(false);
  const [value, setValue] = useState("active");
  const [isLoading, setIsLoading] = useState(false);
  const [refreshCommunitiesData, setRefreshCommunitiesData] = useState(false);
  const [sortOrder, setSortOrder] = useState(SORT_ORDER.DESC);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [leaveModalState, setLeaveModalState] = useState(false);
  const [joinModalState, setJoinModalState] = useState(false);
  const [openNeedPricelistModal, setOpenNeedPricelistModal] = useState(false);
  // The community whose join request the supplier is greeted about — null
  // while there is nothing to greet them with.
  const [joinRequestGreetingCommunity, setJoinRequestGreetingCommunity] =
    useState(null);
  // The community the greeting sent them to answer: the offer can only be
  // opened once the Join requests tab is the live one, so it waits here.
  const [pendingOfferCommunity, setPendingOfferCommunity] = useState(null);
  const greetingCheckedRef = useRef(false);
  const [statsLoaded, setStatsLoaded] = useState(false);
  const [pauseModalState, setPauseModalState] = useState(false);
  const [offersModalType, setOffersModalType] = useState(null);
  const [activeMembers, setActiveMembers] = useState([]);
  const [joinRequests, setJoinRequests] = useState([]);
  const [waitApproval, setWaitApproval] = useState([]);

  const [communityMemberStats, setCommunityMemberStats] = useState({
    activeMembersStat: 1,
    joinRequestsStat: 1,
    waitApprovalStat: 1,
    currentMembersStat: 1,
    maxMembersStat: 1,
  });
  const [maxPage, setMaxPage] = useState({
    1: 1,
    2: 1,
    3: 1,
  });

  const getCommunityManagementStats = useCallback(async () => {
    try {
      const response = await apiGetRequest(endpoints.communityManagmentStats);
      if (response.status === 200) {
        setCommunityMemberStats({
          joinRequestsStat: response?.data?.total_join_requests,
          waitApprovalStat: response?.data?.total_pending_offers,
          activeMembersStat: response?.data?.total_active_communities,
          currentMembersStat: response?.data?.current_members_count,
          maxMembersStat: response?.data?.max_members,
        });
        setStatsLoaded(true);
      }
    } catch (ex) {
      console.log("exception occured while getting stats ", ex);
    }
  }, []);

  useLayoutEffect(() => {
    getCommunityManagementStats();
  }, [getCommunityManagementStats]);

  const getCommunityMembers = async (params) => {
    const { activeTab, activePage, sortOrder } = params;
    try {
      if (activePage === 1) setIsLoading(true);

      const requestParams = {
        type:
          activeTab === 1
            ? "active-communities"
            : activeTab === 2
            ? "join-requests"
            : "waiting-approval",
        pageNumber: activePage || 1,
        pageSize: API_RECORDS_COUNT,
        sort_order: sortOrder,
      };

      const response = await apiGetRequest(
        endpoints.communityManagementMembers(requestParams)
      );

      const newMembers = response?.data?.data || [];
      const maxPage = response?.data?.max_page_number ?? 1;

      if (activeTab === 1) {
        if (activePage === 1) {
          setActiveMembers(newMembers);
        } else {
          setActiveMembers((prevMembers) => {
            const allMembers = [...prevMembers, ...newMembers];
            return allMembers;
          });
        }

        setMaxPage((prev) => ({
          ...prev,
          1: maxPage,
        }));
      } else if (activeTab === 2) {
        if (activePage === 1) {
          setJoinRequests(newMembers);
        } else {
          setJoinRequests((prevRequests) => {
            const allRequests = [...prevRequests, ...newMembers];
            return allRequests;
          });
        }
        setMaxPage((prev) => ({ ...prev, 2: maxPage }));
      } else {
        if (activePage === 1) {
          setWaitApproval(newMembers);
        } else {
          setWaitApproval((prevApprovals) => {
            const allApprovals = [...prevApprovals, ...newMembers];
            return allApprovals;
          });
        }
        setMaxPage((prev) => ({ ...prev, 3: maxPage }));
      }
    } catch (error) {
      // ⚠️ TEMP DEV-PHASE PREVIEW — the KYC preview runs without a backend,
      // so the invitations tab falls back to a sample invitation rather than an
      // empty page. Remove with the preview seed.
      if (readKycPreviewFlag() && activeTab === 2) {
        setJoinRequests(PREVIEW_JOIN_REQUESTS);
        setMaxPage((prev) => ({ ...prev, 2: 1 }));
      }
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCommunityMembersCallback = useCallback(
    async (activePage) => {
      await getCommunityMembers({
        activeTab,
        activePage,
        sortOrder,
      });
    },
    [activeTab, sortOrder]
  );

  const { setContainerRef, resetPagination } = useInfiniteScroll({
    fetchMore: fetchCommunityMembersCallback,
    maxPage: maxPage[activeTab],
    threshold: 200,
    initialPage: 1,
  });

  useEffect(() => {
    resetPagination();
    fetchCommunityMembersCallback(1);
  }, [fetchCommunityMembersCallback, resetPagination]);

  useEffect(() => {
    if (refreshCommunitiesData) {
      resetPagination();
      fetchCommunityMembersCallback(1);
      getCommunityManagementStats();
      setRefreshCommunitiesData(false);
    }
  }, [
    refreshCommunitiesData,
    resetPagination,
    fetchCommunityMembersCallback,
    setRefreshCommunitiesData,
    getCommunityManagementStats,
  ]);

  const toggleSortOrder = () => {
    setSortOrder((prev) =>
      prev === SORT_ORDER.DESC ? SORT_ORDER.ASC : SORT_ORDER.DESC
    );
  };

  const checkPricelists = async () => {
    try {
      // A supplier can join with either a services pricelist OR products.
      const [pricelistRes, productsRes] = await Promise.all([
        apiGetRequest(endpoints.supplierPricelists),
        apiGetRequest(endpoints.supplierProducts),
      ]);
      // Filter out draft pricelists (is_draft === true)
      const validPricelists = (pricelistRes?.data?.pricelists || []).filter(
        (pl) => !pl.is_draft
      );
      const activeProducts = (productsRes?.data?.products || []).filter(
        (p) => p.is_active
      );
      return validPricelists.length > 0 || activeProducts.length > 0;
    } catch (error) {
      console.log("Error occurred while checking pricelists", error);
      return false;
    }
  };

  const openModal = async (supplier, modalType = "join-community") => {
    setSelectedSupplier(supplier);

    if (
      modalType === "join-community" ||
      modalType === "waiting-approval" ||
      modalType === "view-offer"
    ) {
      const hasPricelists = await checkPricelists();
      if (!hasPricelists) {
        setOpenNeedPricelistModal(true);
        return;
      }
    }

    switch (value) {
      case "active":
        if (modalType === "view-offer") {
          setOffersModalType("view");
          setJoinModalState(true);
        } else if (modalType === "pause-community") {
          setPauseModalState(true);
        } else if (modalType === "Leave-community") {
          setLeaveModalState(true);
        }
        break;
      case "join":
        if (modalType === "join-community") {
          setOffersModalType("join");
          setJoinModalState(true);
        } else if (modalType === "ignore-community") {
          setLeaveModalState(true);
        }
        break;
      case "approval":
        if (modalType === "waiting-approval") {
          setOffersModalType("waiting");
          setJoinModalState(true);
        }
        break;
      default:
        setLeaveModalState(true);
    }
  };

  const closeModal = () => {
    setSelectedSupplier(null);
    setOffersModalType(null);
    switch (value) {
      case "active":
        setJoinModalState(false);
        setLeaveModalState(false);
        setPauseModalState(false);
        break;
      case "join":
        setJoinModalState(false);
        setLeaveModalState(false);
        break;
      case "approval":
        setJoinModalState(false);
        break;
      default:
        setLeaveModalState(false);
    }
  };

  const removeCommunity = async (supplier, text) => {
    try {
      if (value === "active") {
        await apiPostRequest(
          endpoints.communityManagementRemoveCommunity(supplier?.id),
          {
            feedback: text,
          }
        );
      } else {
        await apiPostRequest(
          endpoints.communityManagementRemoveCommunity(supplier?.community_id),
          {
            feedback: text,
          }
        );
      }
      showSnackbar(
        `${value === "active" ? supplier?.name : supplier?.community_name} ${t(
          "has_been_removed"
        )}`,
        "success"
      );
      setRefreshCommunitiesData(true);
      closeModal();
    } catch (error) {
      showSnackbar(
        `Failed to remove ${
          value === "active" ? supplier?.name : supplier?.community_name
        }`,
        "error"
      );
      console.log("error occured while removing community ", error);
    }
  };

  const ignoreCommunityRequest = async (supplier, text) => {
    try {
      await apiPostRequest(
        endpoints.communityManagementIgnoreCommunity(supplier?.community_id),
        {
          feedback: text,
        }
      );
      showSnackbar(`${supplier?.community_name} has been ignored`, "success");
      setRefreshCommunitiesData(true);
      closeModal();
    } catch (error) {
      showSnackbar(`Failed to ignore ${supplier?.community_name}`, "error");
      console.log("error occured while ignoring community ", error);
    }
  };

  const activateCommunity = async (supplier) => {
    try {
      await apiPostRequest(
        endpoints.communityManagementActivateCommunity(supplier?.id)
      );
      setActiveMembers((prev) =>
        prev.map((item) =>
          item.id === supplier.id ? { ...item, status: "ACTIVE" } : item
        )
      );
      showSnackbar(`${supplier?.name} has been activated`, "success");
    } catch (error) {
      showSnackbar(`Failed to activate ${supplier?.name}`, "error");
      console.log("Error activating community:", error);
    }
  };

  const pauseCommunity = async (supplier, text) => {
    try {
      await apiPostRequest(
        endpoints.communityManagementPauseCommunity(supplier?.id),
        {
          feedback: text,
        }
      );
      setActiveMembers((prev) =>
        prev.map((item) =>
          item.id === supplier.id ? { ...item, status: "PAUSED_BY_SUPPLIER" } : item
        )
      );
      showSnackbar(`${supplier?.name} has been paused`, "success");
      closeModal();
    } catch (error) {
      showSnackbar(`Failed to pause ${supplier?.name}`, "error");
      console.log("error occured while pausing community ", error);
    }
  };

  // Withdrawing an offer the CM hasn't answered yet. This used to post to
  // /remove, which refuses INITIAL_OFFER_PENDING outright — so cancelling from
  // the waiting-approval tab always failed.
  const cancelOffer = async (supplier) => {
    try {
      await apiPostRequest(
        endpoints.cancelJoinCommunityRequest(supplier?.community_id)
      );
      showSnackbar(`${supplier?.community_name} has been removed`, "success");
      setRefreshCommunitiesData(true);
    } catch (error) {
      showSnackbar(`Failed to remove ${supplier?.community_name}`, "error");
      console.log("error occured while removing community ", error);
    }
  };

  /**
   * Leaving from inside the offer modal (the supplier declined the community's
   * terms of service). Which transition that is depends on where they stand:
   * an approved supplier leaves, one still waiting on the CM withdraws the
   * pending offer — /remove refuses INITIAL_OFFER_PENDING outright.
   */
  const leaveCommunityFromOffer = async (community, feedback) => {
    const communityId = community?.community_id || community?.id;
    const communityName = community?.community_name || community?.name;
    const isWaitingApproval = offersModalType === "waiting";

    try {
      await apiPostRequest(
        isWaitingApproval
          ? endpoints.cancelJoinCommunityRequest(communityId)
          : endpoints.communityManagementRemoveCommunity(communityId),
        { feedback }
      );
      showSnackbar(`${communityName} ${t("has_been_removed")}`, "success");
      setRefreshCommunitiesData(true);
    } catch (error) {
      showSnackbar(`Failed to remove ${communityName}`, "error");
      console.log("error occured while leaving community ", error);
      // Rethrown so the offer modal keeps the confirmation open instead of
      // closing on a leave that never happened.
      throw error;
    }
  };

  const handleJoinSaveRequest = () => {
    if (offersModalType === "view") {
      showSnackbar(
        `${t("join-offer-modal-alerts.update-success_part_1")}${
          selectedSupplier?.name
        } ${t("join-offer-modal-alerts.update-success_part_2")}`,
        "success"
      );
    } else if (offersModalType === "join") {
      showSnackbar(
        `${selectedSupplier?.community_name} ${t(
          "join-offer-modal-alerts.join-success"
        )}`,
        "success"
      );
      setActiveTab(3);
    }
    setRefreshCommunitiesData(true);
  };

  useEffect(() => {
    if (activeTab === 1) {
      setValue("active");
    } else if (activeTab === 2) {
      setValue("join");
    } else if (activeTab === 3) {
      setValue("approval");
    }
  }, [activeTab]);

  // Clean up the invite link's param. The greeting below is what acts on the
  // invite; only the fallback for a supplier whose invite has no matching
  // request left (nothing to send an offer to) lives here.
  useEffect(() => {
    if (!inviteCommunityId || inviteHandledRef.current || isLoading) return;
    if (activeTab !== 2) return;

    inviteHandledRef.current = true;

    searchParams.delete("invite_community_id");
    setSearchParams(searchParams, { replace: true });

    const matchingCommunity = joinRequests.find(
      (req) =>
        String(req.community_id) === String(inviteCommunityId) ||
        String(req.id) === String(inviteCommunityId)
    );

    if (!matchingCommunity) {
      checkPricelists().then((hasPricelists) => {
        if (!hasPricelists) {
          setOpenNeedPricelistModal(true);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteCommunityId, activeTab, joinRequests, isLoading]);

  /**
   * A supplier who joins a community lands on this page with the community's
   * request already waiting for them and nothing saying so — whichever route
   * brought them here (an invite link, or a community manager adding them
   * while they were away). Greet them about the newest request they haven't
   * been greeted about yet, once.
   *
   * The list is fetched on its own because the page only loads the tab the
   * supplier is looking at, and the greeting has to work from the Active tab
   * too. The stats tell us whether that fetch is worth making at all.
   */
  useEffect(() => {
    const supplierId = profileData?.id;
    if (!supplierId || !statsLoaded || greetingCheckedRef.current) return;
    if (!communityMemberStats.joinRequestsStat) return;

    greetingCheckedRef.current = true;

    const greetPendingJoinRequest = async () => {
      try {
        const response = await apiGetRequest(
          endpoints.communityManagementMembers({
            type: "join-requests",
            pageNumber: 1,
            pageSize: API_RECORDS_COUNT,
            sort_order: SORT_ORDER.DESC,
          })
        );

        const requests = response?.data?.data || [];
        if (requests.length === 0) return;

        const greeted = getGreetedJoinRequestKeys(supplierId);
        // Arriving from an invite link is about one specific community, so
        // that request wins over whichever one happens to be newest.
        const arrivedFor = arrivedForCommunityRef.current;
        const invited = arrivedFor
          ? requests.find(
              (req) => String(req.community_id) === String(arrivedFor)
            )
          : null;
        const target =
          invited ||
          requests.find((req) => !greeted.includes(getJoinRequestKey(req)));

        if (!target) return;

        markJoinRequestGreeted(supplierId, getJoinRequestKey(target));
        setJoinRequestGreetingCommunity(target);
      } catch (error) {
        // The greeting is a nicety — a failed fetch must not break the page.
        console.log("Error occurred while checking join requests", error);
      }
    };

    greetPendingJoinRequest();
  }, [
    profileData?.id,
    statsLoaded,
    communityMemberStats.joinRequestsStat,
    inviteCommunityId,
  ]);

  /**
   * The offer modal reads the list the supplier is currently on, so it can
   * only be opened once the Join requests tab has taken over.
   */
  useEffect(() => {
    if (!pendingOfferCommunity || value !== "join") return;

    const community = pendingOfferCommunity;
    setPendingOfferCommunity(null);
    openModal(community, "join-community");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingOfferCommunity, value]);

  const closeJoinRequestGreeting = () => {
    setJoinRequestGreetingCommunity(null);
  };

  const handleJoinRequestGreetingAction = () => {
    const community = joinRequestGreetingCommunity;
    setJoinRequestGreetingCommunity(null);
    if (!community) return;
    // "Let's go" is a promise to take them to the request — the Join requests
    // tab, with the offer they have to send already open on it.
    setActiveTab(2);
    setPendingOfferCommunity(community);
  };

  const closeNeedPricelistModal = () => {
    setOpenNeedPricelistModal(false);
  };

  const handleNeedPricelistActionClick = () => {
    navigate("/manage-pricelists");
  };

  return (
    <>
      <Box
        sx={{
          marginTop: {
            xs: "75px",
            md: "0px",
          },
          display: "flex",
          flexDirection: "column",
          flex: 1,
          overflow: "hidden",
        }}
      >
        <ContentBody
          {...{
            isLoading,
            activeMembersData: activeMembers,
            joinRequestsData: joinRequests,
            waitApprovalData: waitApproval,
            openModal,
            sortOrder,
            toggleSortOrder,
            activeTab,
            setActiveTab,
            activateCommunity,
            setContainerRef,
            communityMemberStats,
          }}
        />
        <LeaveCommunityModal
          supplier={selectedSupplier}
          handleOk={
            value === "active"
              ? removeCommunity
              : value === "approval"
              ? removeCommunity
              : ignoreCommunityRequest
          }
          handleClose={() => setLeaveModalState(false)}
          open={leaveModalState}
          value={value}
        />
        {pauseModalState && selectedSupplier && (
          <PauseCommunityModal
            supplier={selectedSupplier}
            handleOk={pauseCommunity}
            open={pauseModalState}
            handleClose={() => setPauseModalState(false)}
            value={"Pause"}
          />
        )}
        <SupplierJoinOffersModal
          {...{
            open: joinModalState,
            onClose: closeModal,
            communityData: selectedSupplier,
            viewType: offersModalType,
            cancelOffer: cancelOffer,
            onLeaveCommunity: leaveCommunityFromOffer,
            onActionClick: handleJoinSaveRequest,
            acceptJoinRequest: offersModalType === "join",
          }}
        />

        <JoinRequestReceivedModal
          open={Boolean(joinRequestGreetingCommunity)}
          onClose={closeJoinRequestGreeting}
          onActionClick={handleJoinRequestGreetingAction}
          communityName={
            joinRequestGreetingCommunity?.community_name ||
            joinRequestGreetingCommunity?.name ||
            ""
          }
        />

        <NeedPricelistModal
          open={openNeedPricelistModal}
          onClose={closeNeedPricelistModal}
          onActionClick={handleNeedPricelistActionClick}
        />
      </Box>
    </>
  );
};

export default MyCommunitiesContainer;
